"use client";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import edit from "../../public/Edit-profile.png";
import cover from "../../public/cover-img.png";
import profile2 from "../../public/profile3.png";
import userIcon from "../../public/user-icon.svg";
import mail from "../../public/mail-icon.png";
import unlock from "../../public/unlock-icon.png";
import globe from "../../public/Form-icon.png";
import { Map, MapPin, Pin, User, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/lib/redux/store";
import { getSingleUser, updateUser, forgotPassword } from "@/lib/redux/slice/authSlice";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Loader from "./Loader";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  profileSettingsSchema,
  ProfileValidationType,
} from "@/validations/profileSettingsSchema";

interface ProfileSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProfileSettingsModal({
  isOpen,
  onClose,
}: ProfileSettingsModalProps) {
  const { 
    register, 
    handleSubmit, 
    setValue, 
    reset,
    formState: { errors, isValid } 
  } = useForm<ProfileValidationType>({
    resolver: yupResolver(profileSettingsSchema),
    mode: "onBlur", 
  });

  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [showImage, setShowImage] = useState<string | undefined>(undefined);
  const dispatch = useDispatch<AppDispatch>();
  const { loading, user } = useSelector((state: RootState) => state.auth);
  const modalRef = useRef<HTMLDivElement>(null); 
  
  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setProfileImage(event.target.files[0]);
      setShowImage(URL.createObjectURL(event.target.files[0]));
    }
  };

  const onSubmit = async (data: ProfileValidationType) => {
    try {
      if (user) {
        const formData = new FormData();
        formData.append("name", data.name || "");
        formData.append("email", data.email);
        formData.append("country", data.country || "");
        formData.append("state", data.state || "");
        formData.append("city", data.city || "");
        formData.append("gender", data.gender || ""); 
        formData.append("pin_code", data.pin_code || "");

        if (profileImage) {
          formData.append("profile_image", profileImage);
        }
        
        await dispatch(updateUser({ id: user._id, userData: formData })).unwrap();
        
        toast.success("Update successful!");
        onClose(); 
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("An unknown error occurred!");
      }
    }
  };

  const handleResetPasswordClick = async () => {
    if (!user?.email) {
      toast.error("Could not find your email. Please log in again.");
      return;
    }
    
    try {
      const result = await dispatch(forgotPassword({ email: user.email })).unwrap();
      toast.success(result.message);
      onClose(); 
    } catch (error: any) {
      toast.error(error as string);
    }
  };

  // Helper: build correct image src
  const buildImageSrc = (path?: string | null) => {
    if (!path) return undefined;
    // if already absolute URL, use as-is
    if (/^https?:\/\//i.test(path)) return path;
    // otherwise prefix with API base (ensure no double slashes)
    const API = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/$/, "");
    const rel = path.startsWith("/") ? path : `/${path}`;
    return API ? `${API}${rel}` : rel;
  };

  useEffect(() => {
    if (isOpen && user) {
      // a. Populate the form fields
      setValue("name", user.name || "");
      setValue("email", user.email || "");
      setValue("country", user.country || "");
      setValue("state", user.state || "");
      setValue("city", user.city || "");
      setValue("gender", (user.gender as ProfileValidationType["gender"]) || ""); 
      setValue("pin_code", user.pin_code || "");

      // b. Set the profile image (handle absolute S3 urls and relative paths)
      if (user.images && user.images.length > 0) {
        setShowImage(buildImageSrc(user.images[0].path));
      } else {
        setShowImage(undefined); 
      }
    }

    // c. Handle click outside to close
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose(); 
      }
    };
    
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, user, onClose, setValue]); 

  if (!isOpen) return null;

  // If showImage is an external URL (http/https), mark as external to avoid Next image optimization
  const isExternal = !!showImage && /^https?:\/\//i.test(showImage);

  // --- FIX: allow saving when form is valid OR when a new profileImage was selected ---
  const canSave = isValid || !!profileImage;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
      {loading === true ? <Loader /> : ""}
      <div
        ref={modalRef} 
        className="bg-white rounded-3xl w-full max-w-md md:max-w-lg lg:max-w-xl max-h-[570px] overflow-y-auto custom-scrollbar animate-in fade-in duration-300 relative px-7 pt-10 pb-[40px]"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 z-10"
        >
          <X size={24} />
        </button>

        {/* Cover Photo Section */}
        <div className="relative h-32">
          <Image
            src={cover}
            alt="Cover"
            fill
            className="object-cover rounded-t-3xl"
            priority
          />
          <div className="absolute -bottom-10 left-8 sm:left-10 md:left-12">
            <div className="relative">
              <Image
                src={showImage || profile2}
                alt="Profile"
                width={110}
                height={110}
                className="rounded-full border-4 border-white object-contain bg-white w-[110px] h-[110px]"
                unoptimized={isExternal}
              />
              <label
                htmlFor="profilePhotoUpload"
                className="absolute bottom-0 right-0 bg-white rounded-full p-1 cursor-pointer"
              >
                <Image src={edit} alt="edit" width={22} />
              </label>
              <input
                id="profilePhotoUpload"
                type="file"
                className="hidden"
                onChange={handleImageChange}
              />
            </div>
          </div>
        </div>

        <div className="bg-[#E6F4FF] rounded-b-2xl p-7 mb-4 custom-shadow">
          <h2 className="text-[23px] font-medium mb-1 pt-8 text-[#0B0B0B]">
            Your Photo
          </h2>
          <p className="text-[#3A3A3A] text-[18px] mb-3">
            This will be displayed on your profile
          </p>
        </div>

        {/* Personal Information Section */}
        <div className="pt-10">
          <div className="bg-[#E6F4FF] rounded-2xl p-7 mb-4 custom-shadow">
            <h2 className="text-[23px] font-medium text-[#0B0B0B] mb-4">
              Personal Information
            </h2>
            <form className="space-y-3" onSubmit={handleSubmit(onSubmit)}>
              <div className="relative mb-5">
                <Image
                  src={userIcon}
                  className="absolute left-4 top-3"
                  alt=""
                />
                <input
                  type="text"
                  placeholder="Name"
                  className="w-full pl-11 pr-4 py-2 bg-transparent border-[#7D7999]  rounded-full text-sm border "
                  {...register("name")}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs ml-5 mt-1">{errors.name.message}</p>
                )}
              </div>
              <div className="relative mb-5">
                <Image src={mail} alt="" className="absolute left-4 top-3" />
                <input
                  type="email"
                  placeholder="E-mail"
                  className="w-full pl-11 pr-4 py-2 bg-transparent border-[#7D7999]  rounded-full text-sm border "
                  {...register("email")}
                  disabled
                />
                {errors.email && (
                  <p className="text-red-500 text-xs ml-5 mt-1">{errors.email.message}</p>
                )}
              </div>
              
              {/* --- THIS IS THE FIX --- */}
              {/* This replaces the disabled password input with a button */}
              <div className="relative mb-5 flex items-center border border-[#7D7999] rounded-full px-3 py-2">
                <Image
                  src={unlock}
                  alt="unlock"
                  className="absolute left-4 top-3"
                />
                <span className="w-full pl-11 pr-4 py-0 text-gray-400 text-sm">
                  Password
                </span>
                <button
                  type="button" 
                  className="text-[#0094FF] hover:text-[#0080E0] font-medium text-sm whitespace-nowrap"
                  onClick={handleResetPasswordClick}
                >
                  Send Password Reset Link
                </button>
              </div>
              {/* --- END OF FIX --- */}

              <div className="relative mb-5">
                <Image
                  src={globe}
                  alt="globe"
                  className="absolute left-4 top-3"
                />
                <input
                  type="text"
                  placeholder="Country"
                  className="w-full pl-11 pr-4 py-2 bg-transparent border-[#7D7999]  rounded-full text-sm border "
                  {...register("country")}
                />
                {errors.country && (
                  <p className="text-red-500 text-xs ml-5 mt-1">{errors.country.message}</p>
                )}
              </div>
              <div className="relative mb-5">
                <Map size={18} className="absolute left-4 top-3" />
                <input
                  type="text"
                  placeholder="State"
                  className="w-full pl-11 pr-4 py-2 bg-transparent border-[#7D7999] rounded-full text-sm border "
                  {...register("state")}
                />
                {errors.state && (
                  <p className="text-red-500 text-xs ml-5 mt-1">{errors.state.message}</p>
                )}
              </div>
              <div className="relative mb-5">
                <MapPin size={18} className="absolute left-4 top-3" />
                <input
                  type="text"
                  placeholder="City"
                  className="w-full pl-11 pr-4 py-2 bg-transparent border-[#7D7999] rounded-full text-sm border "
                  {...register("city")}
                />
                {errors.city && (
                  <p className="text-red-500 text-xs ml-5 mt-1">{errors.city.message}</p>
                )}
              </div>
              <div className="relative mb-5 custom-select">
                <User size={18} className="absolute left-4 top-3" />
                <select
                  className="w-full pl-11 pr-4 py-2 bg-transparent border-[#7D7999] rounded-full text-sm border "
                  {...register("gender")}
                >
                  <option value="" disabled>
                    Select gender *
                  </option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {errors.gender && (
                  <p className="text-red-500 text-xs ml-5 mt-1">{errors.gender.message}</p>
                )}
              </div>
              <div className="relative mb-5">
                <Pin size={18} className="absolute left-4 top-3" />
                <input
                  type="text"
                  placeholder="Pin Code"
                  className="w-full pl-11 pr-4 py-2 bg-transparent border-[#7D7999] rounded-full text-sm border "
                  {...register("pin_code")}
                />
                {errors.pin_code && (
                  <p className="text-red-500 text-xs ml-5 mt-1">{errors.pin_code.message}</p>
                )}
              </div>
              <div className="flex justify-end pt-2">
                <button
                  className={`px-5 py-2 text-white rounded-[4px] text-sm font-semibold transition-colors text-[18px] ${
                    !canSave 
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[#0094FF] hover:bg-[#0080E0]'
                  }`}
                  type="submit"
                  disabled={!canSave}
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
